import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EntityActionOptions, EntityCollectionServiceBase, EntityCollectionServiceElementsFactory } from '@ngrx/data';
import buildQuery from 'odata-query';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { ConfirmDeleteDialogComponent } from '../components/shared/confirm-delete-dialog/confirm-delete-dialog.component';
import { ShippingOrderUploadDialogComponent } from '../components/shipping-order-upload-dialog/shipping-order-upload-dialog.component';
import { ShippingOrderDialogComponent } from '../components/shipping-orders/shipping-order-dialog.component';
import { ShippingOrder, ShippingOrderAttachment, ShippingOrderExtended, ShippingOrderStatus } from '../models';
import { ProjectsService } from './projects.service';
import { SitesService } from './sites.service';

const defaultExpansion = `project($select=code),shippingLocation`;

@Injectable({ providedIn: 'root' })
export class ShippingOrdersService extends EntityCollectionServiceBase<ShippingOrderExtended> {
  constructor(
    serviceElementsFactory: EntityCollectionServiceElementsFactory,
    private projectsService: ProjectsService,
    public dialog: MatDialog,
    private http: HttpClient,
    private sitesService: SitesService,
  ) {
    super('ShippingOrder', serviceElementsFactory);
  }

  add(entity: ShippingOrderExtended, options?: EntityActionOptions) {
    const query = super.add(entity, options);
    query.subscribe();
    return query;
  }
  delete(entity: string | number | ShippingOrderExtended, options?: EntityActionOptions) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent);
    const delete$ = dialogRef.afterClosed().pipe(
      switchMap((confirmation) => {
        if (confirmation) {
          return super.delete(entity as any, options);
        } else {
          return of(null);
        }
      }),
    );
    delete$.subscribe();
    return delete$;
  }
  deleteAttachment(attachmentId: number) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent);
    return dialogRef.afterClosed().pipe(
      switchMap((confirmation) => {
        if (confirmation) {
          return this.http.delete(`/api/ShippingOrderAttachments/${attachmentId}`).pipe(map(() => true));
        } else {
          return of(false);
        }
      }),
    );
  }
  getAttachmentsByShippingOrderId(shippingOrderId: number) {
    const query = buildQuery({ filter: { shippingOrderId: shippingOrderId } });
    return this.http.get<ShippingOrderAttachment[]>(`/api/ShippingOrderAttachments${query}`);
  }
  getById(id: number) {
    const query = super.getWithQuery({
      $filter: `id eq ${id}`,
    });
    query.subscribe();
    return query.pipe(map(([item]) => item));
  }
  getEntities(active = true) {
    const query = super.getWithQuery({});
    this.setLoading(true);
    query.subscribe((items) => {
      if (active) {
        this.setLoaded(true);
      }
      this.setLoading(false);
    });
    return query.pipe(map((items) => items));
  }
  update(entity: ShippingOrderExtended, options?: EntityActionOptions) {
    return super.update(entity, options);
  }
  openNewDialog(): Observable<ShippingOrder> {
    return combineLatest([
      this.projectsService.entities$.pipe(filter((e) => e.length > 0)),
      this.sitesService.userSites$.pipe(filter((e) => e.length > 0)),
    ]).pipe(
      first(),
      switchMap(([projects, sites]) => {
        const dialogRef = this.dialog.open(ShippingOrderDialogComponent, {
          disableClose: true,
          minWidth: '70%',
          data: {
            title: 'Create Shipping Order',
            projects,
            sites,
            shippingOrder: {
              id: undefined,
              notes: null,
              number: null,
              projectId: null,
              shippingDate: null,
              siteId: sites[0]?.id,
              status: ShippingOrderStatus.Pending,
              trackingNumber: null,
              vendorId: null,
            },
          },
        });

        return dialogRef.afterClosed();
      }),
      switchMap((result) => {
        if (result) {
          return this.add(result);
        } else {
          return of(result);
        }
      }),
    );
  }

  openEditDialog(shippingOrder: ShippingOrderExtended): Observable<ShippingOrderExtended> {
    return combineLatest([
      this.projectsService.entities$.pipe(filter((e) => e.length > 0)),
      this.sitesService.userSites$.pipe(filter((e) => e.length > 0)),
    ]).pipe(
      first(),
      switchMap(([projects, sites]) => {
        const dialogRef = this.dialog.open(ShippingOrderDialogComponent, {
          minWidth: '70%',
          disableClose: true,
          data: { shippingOrder, title: 'Edit Shipping Order', projects, sites },
        });

        return dialogRef.afterClosed();
      }),
      switchMap((result) => {
        if (result) {
          return this.update(result);
        } else {
          of(result);
        }
      }),
    );
  }

  openUploadAttachmentDialog(shippingOrder: ShippingOrderExtended) {
    const dialogRef = this.dialog.open(ShippingOrderUploadDialogComponent, {
      minWidth: '70%',
      disableClose: true,
      data: { shippingOrder, shippingOrderService: this },
    });

    return dialogRef.afterClosed();
  }

  uploadAttachments(shippingOrderId: number, files: Set<File>): { [key: string]: { progress: Observable<number> } } {
    // this will be the our resulting map
    const status: { [key: string]: { progress: Observable<number> } } = {};

    files.forEach((file) => {
      // create a new multipart-form for every file
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);

      // create a http-post request and pass the form
      // tell it to report the upload progress
      const req = new HttpRequest('POST', `/api/ShippingOrderAttachments/${shippingOrderId}`, formData, {
        reportProgress: true,
      });

      // create a new progress-subject for every file
      const progress = new Subject<number>();

      // send the http-request and subscribe for progress-updates
      this.http.request(req).subscribe((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          // calculate the progress percentage

          const percentDone = Math.round((100 * event.loaded) / event.total);
          // pass the percentage into the progress-stream
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          // Close the progress-stream if we get an answer form the API
          // The upload is complete
          progress.complete();
        }
      });

      // Save every progress-observable in a map of all observables
      status[file.name] = {
        progress: progress.asObservable(),
      };
    });

    // return the map of progress.observables
    return status;
  }
}
